import React, { useState, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';

import type { IChatInputProps, TagChip } from './ChatInput.types';
import { Document } from '../DocumentsPanel/DocumentsPanel.types';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { Text } from '../Text/Text';
import { ProgressBar } from '../ProgressBar/ProgressBar';
import { IconName } from '@ui-library/Icon/Icon.type';
import { FileTypeIcon } from '../DocumentsPanel/components/FileTypeIcon';
import { ModelSelector, ModelPreference } from '../ModelSelector';
import { InteractionModeSelector, InteractionMode } from '../InteractionModeSelector';
import { AgentSettingsMenu, DEFAULT_AGENT_SETTINGS } from '../AgentSettingsMenu';
import { TagSuggestionList, TagSuggestionListHandle } from './TagSuggestionList';
import { parseTagsFromContent, stripTagMarkers } from '../../utils/cabTagSerializer';
import { generateUUID } from '../../utils/uuid';

import api from '../../utils/axios';
import { getProjectTierPicks } from '../../services/projectLlm';
import { formatArtifactTitle } from '../../utils/parseArtifacts';
import {
  isImageFile,
  isDocumentFile,
  validateFilesForUpload,
  fetchBuildUploadPolicy,
  checkBuildUploads,
} from '../../utils/fileUploadConfig';

import { FileUploadDialog } from '../Dialogs/FileUploadDialog';

const FilePreview = ({ files, onRemove }: { files: Document[]; onRemove: (id: string) => void }) => {
  return (
    <div className="flex flex-wrap gap-bg-v2-sm mb-bg-v2-md">
      {files.map(file => (
        <div key={file.id} className="relative group">
          <div className="flex items-center gap-bg-v2-xs rounded-bg-v2-md px-bg-v2-md py-bg-v2-sm bg-bg-secondary border border-border-secondary">
            <FileTypeIcon extension={file.fileType} size={16} />
            <span className="text-bg-v2-xs font-bg-v2-medium text-text-primary max-w-[120px] truncate">{file.source === 'artifact' ? formatArtifactTitle(file.name, file.version) : file.name}</span>
            <button
              onClick={() => onRemove(file.id)}
              className="ml-bg-v2-xs text-text-tertiary hover:text-text-error-primary transition-colors">
              <Icon name={IconName.Close} size="xs" variant="secondary" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export const ChatInput: React.FC<IChatInputProps> = ({
  value: controlledValue,
  onChange,
  onSend,
  onStop,
  isStopping = false,
  placeholder = 'Type your message...',
  disabled = false,
  isStreaming = false,
  isSending = false,
  className = '',
  projectId = '',
  showAttachmentButton = true,
  requireTextToSend = false,
  allowedDocumentExtensions,
  attachmentPolicy = 'default',
  showModelSelector = false,
  model: controlledModel,
  onModelChange,
  enableTagChips = false,
  onTagSearch,
  onTagsChange,
  tagSuggestions: controlledTagSuggestions,
  isTagSearching = false,
  showAgentSettings = false,
  agentSettings: controlledAgentSettings,
  onAgentSettingsChange,
  // Queue props
  isQueueMode = false,
  onQueue,
  editingJobId = null,
  onSaveEdit,
  onCancelEdit,
  editingAttachments,
  // Interaction mode props
  showInteractionMode = false,
  interactionMode = InteractionMode.AGENT,
  onInteractionModeChange,
  interactionModeLockedToAsk = false,
}) => {
  // A chat with no queue (Prototype) still gets Stop while it streams.
  const isStopOnly = !isQueueMode && isStreaming && !!onStop;
  const [internalValue, setInternalValue] = useState('');
  const [allDocuments, setAllDocuments] = useState<Document[]>([]);
  const [showFileUpoloadDialog, setShowFileUpoloadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isDragOverTextarea, setIsDragOverTextarea] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
  // Percent of bytes sent, for Build-mode uploads; null when not tracked.
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [internalModel, setInternalModel] = useState<ModelPreference>(ModelPreference.MEDIUM);
  const [internalAgentSettings, setInternalAgentSettings] = useState<Record<string, boolean>>(DEFAULT_AGENT_SETTINGS);
  const [tagChips, setTagChips] = useState<TagChip[]>([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [internalTagSuggestions, setInternalTagSuggestions] = useState<TagChip[]>([]);
  const [selectedTagIndex, setSelectedTagIndex] = useState(0);
  const [cursorRect, setCursorRect] = useState<{ top: number; left: number } | null>(null);

  const isTagSuggestionsControlled = controlledTagSuggestions !== undefined;
  const tagSuggestions = isTagSuggestionsControlled ? controlledTagSuggestions : internalTagSuggestions;
  const setTagSuggestions = isTagSuggestionsControlled ? () => {} : setInternalTagSuggestions;
  
  const isModelControlled = controlledModel !== undefined;
  const model = isModelControlled ? controlledModel : internalModel;

  const isAgentSettingsControlled = controlledAgentSettings !== undefined;
  const agentSettings = isAgentSettingsControlled ? controlledAgentSettings : internalAgentSettings;

  const ALL_MODEL_PREFERENCES = [
    { value: ModelPreference.MEDIUM, label: 'Medium', description: 'Balanced speed & quality' },
    { value: ModelPreference.HIGH, label: 'High', description: 'Best quality output' },
  ];

  /**
   * Which tiers this project's LLM can actually serve.
   *
   * `null` means "not known yet" and an empty array means "this feature has
   * no tier control at all". Both leave every option selectable — the same
   * behaviour as before the picks existed. Only a non-empty list mutes
   * anything, so "serves one tier" and "has no picker" stay distinguishable.
   *
   * A channel that serves Medium but not High (GLM on a Bedrock account
   * without GLM-5) would otherwise show a High button that silently resolves
   * to Medium, so the server's answer is what decides, never this list.
   */
  const [allowedTiers, setAllowedTiers] = useState<string[] | null>(null);

  useEffect(() => {
    if (!showModelSelector || !projectId) return;
    let cancelled = false;
    getProjectTierPicks(projectId)
      .then((picks) => {
        if (!cancelled) setAllowedTiers(picks);
      })
      // Show everything rather than nothing: the resolver degrades a tier the
      // channel cannot serve and records why, so an over-wide picker is
      // recoverable where a fully greyed one is a dead control.
      .catch(() => {
        if (!cancelled) setAllowedTiers(null);
      });
    return () => {
      cancelled = true;
    };
  }, [showModelSelector, projectId]);

  /**
   * A tier the project's LLM cannot serve must not stay selected. The server
   * would degrade it and record why, so nothing breaks, but the trigger would
   * read "Medium" while `high` went out on the request. Move the selection to
   * the first tier that is actually served.
   */
  useEffect(() => {
    // Empty means "no control", not "nothing available" - nothing to correct.
    if (allowedTiers === null || allowedTiers.length === 0) return;
    if (allowedTiers.includes(model)) return;
    const fallback = ALL_MODEL_PREFERENCES.find((o) => allowedTiers.includes(o.value));
    if (!fallback) return;
    if (isModelControlled && onModelChange) {
      onModelChange(fallback.value);
    } else {
      setInternalModel(fallback.value);
    }
    // ALL_MODEL_PREFERENCES is rebuilt each render; its contents are constant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedTiers, model, isModelControlled, onModelChange]);

  /**
   * Every tier stays in the list. One the project's LLM cannot serve is
   * marked `unavailable`, which renders it muted and unselectable: a tier
   * that disappears reads as a bug, where a greyed one with a reason reads
   * as a fact about the deployment's configuration.
   *
   * The reason replaces the option's description in the row, so it is
   * read as a sentence about that tier.
   */
  const MODEL_PREFERENCES = ALL_MODEL_PREFERENCES.map((option) =>
    allowedTiers === null ||
    allowedTiers.length === 0 ||
    allowedTiers.includes(option.value)
      ? option
      : {
          ...option,
          unavailable: true,
          unavailableReason: 'Tier not available for selected LLM service',
        }
  );

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const editableRef = useRef<HTMLDivElement>(null);
  const suggestionsRef = useRef<TagSuggestionListHandle>(null);
  const suggestionsContainerRef = useRef<HTMLDivElement>(null);
  const uploadAbortController = useRef<AbortController | null>(null);
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  // Sync editing attachments into allDocuments when editing mode starts
  useEffect(() => {
    if (editingJobId) {
      if (editingAttachments && editingAttachments.length > 0) {
        setAllDocuments(
          editingAttachments.map(att => ({
            id: att.id,
            name: att.name,
            source: att.source as 'file' | 'artifact' | 'image' | 'cab_markdown',
            fileType: att.name.split('.').pop() || '',
            type: att.type,
            size: 0,
            dateModified: new Date().toISOString(),
          }))
        );
      } else {
        setAllDocuments([]);
      }
    } else {
      // Clear documents when exiting edit mode
      setAllDocuments([]);
    }
  }, [editingJobId, editingAttachments]);

  // On entering edit mode, hydrate ticket/epic chips from any {{ticket|epic:...}}
  // tokens in the loaded value — reusing the same parser the transcript uses to
  // render chips — then normalize the value to `@label` text so the chips render and
  // a re-save can re-serialize them. Keyed on editingJobId so we seed once on entry
  // and don't clobber the user's chip edits mid-session.
  useEffect(() => {
    if (!enableTagChips) return;
    if (!editingJobId) { setTagChips([]); return; }

    const { tags } = parseTagsFromContent(value);
    const refTags = tags.filter(t => t.kind === 'ticket' || t.kind === 'epic');
    if (refTags.length === 0) { setTagChips([]); return; }

    const chips: TagChip[] = refTags.map(t => ({
      id: t.id,
      label: t.label,
      sectionName: t.kind === 'epic' ? 'Epics' : 'Tickets',
      data: { type: t.kind, ticketId: t.ticketId, epicId: t.epicId },
    }));
    setTagChips(chips);
    onTagsChange?.(chips);

    // Convert the loaded token text to `@label` so renderContentEditable can chip it.
    const stripped = stripTagMarkers(value);
    if (stripped !== value) onChange?.(stripped);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingJobId]);

  // Sync contentEditable with value when using tag chips
  const prevEditingJobIdRef = useRef<string | null>(editingJobId);
  useEffect(() => {
    const editEnded = prevEditingJobIdRef.current !== null && editingJobId === null;
    prevEditingJobIdRef.current = editingJobId;

    if (!enableTagChips || !editableRef.current) return;
    const editable = editableRef.current;

    // Edit mode just ended. Every exit empties the box — Send, Queue and Save
    // Changes call clearInput(), Cancel Edit moves focus to its button so the
    // guard below lets the clear through. But when the parent ends it (the queued
    // prompt started running while being edited) nothing moves focus, so the
    // guard would skip the clear and leave the edited text in the DOM: drawn
    // under the placeholder, since value is now empty, and serialised into
    // whatever is typed next. Ending edit mode isn't the user typing, so the
    // guard doesn't apply to it.
    if (editEnded) {
      editable.innerHTML = '';
      if (!isControlled) setInternalValue('');
      return;
    }

    // Never fight the user mid-type.
    if (document.activeElement === editable) return;

    if (!isControlled || editingJobId) {
      // Uncontrolled: keep the DOM in sync with internal value + chips (original
      // behavior). Controlled + editing: render the loaded queued message into the box.
      renderContentEditable();
    } else if (!value) {
      // Controlled, not editing: only ever CLEAR (after send / queue / cancel).
      // Never repopulate from a non-empty `value` here — during send/queue the
      // controlled value briefly lags the DOM clear, and repopulating it would make
      // the just-sent message reappear in the input (showing twice: box + queue card).
      editable.innerHTML = '';
    }
  }, [tagChips, enableTagChips, value, editingJobId, isControlled]);

  // Handle controlled tag suggestions - only open, never force close
  useEffect(() => {
    if (isTagSuggestionsControlled && isTagSearching) {
      setShowTagSuggestions(true);
    }
  }, [isTagSuggestionsControlled, isTagSearching]);

  // Update selected index when suggestions change
  useEffect(() => {
    if (isTagSuggestionsControlled && tagSuggestions.length > 0 && showTagSuggestions) {
      setSelectedTagIndex(0);
    }
  }, [isTagSuggestionsControlled, tagSuggestions, showTagSuggestions]);

  // Close suggestions dropdown when clicking outside
  useEffect(() => {
    if (!showTagSuggestions) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isInsideSuggestions = suggestionsContainerRef.current?.contains(target);
      const isInsideEditable = editableRef.current?.contains(target);

      if (!isInsideSuggestions && !isInsideEditable) {
        setShowTagSuggestions(false);
        setCursorRect(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTagSuggestions]);

  const renderContentEditable = () => {
    const editable = editableRef.current;
    if (!editable) return;

    editable.innerHTML = '';
    let remainingText = value;
    const sortedTags = [...tagChips].sort((a, b) => {
      const aIndex = value.indexOf('@' + a.label);
      const bIndex = value.indexOf('@' + b.label);
      return aIndex - bIndex;
    });

    sortedTags.forEach(tag => {
      const tagText = '@' + tag.label;
      const index = remainingText.indexOf(tagText);
      
      if (index !== -1) {
        // Add text before tag
        if (index > 0) {
          editable.appendChild(document.createTextNode(remainingText.substring(0, index)));
        }

        const chip = document.createElement('span');
        chip.contentEditable = 'false';
        chip.setAttribute('data-tag-id', tag.id);
        chip.className = 'inline-flex items-center gap-1 bg-bg-secondary border border-text-primary rounded px-1.5 py-0.5 mx-0.5 my-0.5 text-xs font-medium text-text-primary font-mono align-middle';
        chip.style.verticalAlign = 'middle';

        const chipContent = document.createElement('span');
        chipContent.textContent = tag.label;
        chip.appendChild(chipContent);

        const deleteBtn = document.createElement('span');
        deleteBtn.innerHTML = '×';
        deleteBtn.className = 'cursor-pointer hover:text-text-error-primary ml-0.5 text-sm leading-none';
        deleteBtn.contentEditable = 'false';
        deleteBtn.setAttribute('data-delete-tag', tag.id);
        chip.appendChild(deleteBtn);

        editable.appendChild(chip);
        remainingText = remainingText.substring(index + tagText.length);
      }
    });

    // Add remaining text
    if (remainingText) {
      editable.appendChild(document.createTextNode(remainingText));
    }
  };

  // Serialize the contentEditable DOM back to plain text, matching what Chrome
  // produces natively. Safari (WebKit) wraps newlines in <div>/<br> block elements
  // instead of inserting "\n" text nodes, so a flat, direct-children-only walk drops
  // any content — including a pasted URL — that lands inside one of those blocks.
  // This walks recursively: block elements (<div>/<p>) and <br> become "\n",
  // tag chips become "@label", and bare text (including Chrome's "\n" newlines) is
  // kept as-is. Result is identical to the old walk for the flat Chrome DOM.
  const serializeEditable = (root: HTMLElement): { text: string; tags: TagChip[] } => {
    const tags: TagChip[] = [];
    const BLOCK_TAGS = new Set(['DIV', 'P']);

    const walk = (node: Node): string => {
      let out = '';
      // Whether this level has produced any line content yet — including an
      // EMPTY block (blank line). Deciding block separation from `out.length`
      // would eat leading blank lines: <div><br></div> yields no text but is
      // still a line, so the block after it must be preceded by "\n".
      let sawLine = false;
      const children = Array.from(node.childNodes);
      children.forEach((child, i) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const textPart = child.textContent || '';
          out += textPart;
          if (textPart.length > 0) sawLine = true;
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          const el = child as HTMLElement;
          const tagId = el.getAttribute('data-tag-id');
          if (tagId) {
            const tag = tagChips.find(t => t.id === tagId);
            if (tag) {
              out += '@' + tag.label;
              tags.push(tag);
              sawLine = true;
            }
          } else if (el.tagName === 'BR') {
            // A <br> that is the last child of its parent is a WebKit editing
            // filler (added to give a block height), not a real newline — skip it.
            if (i !== children.length - 1) {
              out += '\n';
              sawLine = true;
            }
          } else if (BLOCK_TAGS.has(el.tagName)) {
            // Each block element is one visual line: separate it from whatever
            // came before — even a blank line that produced no text.
            if (sawLine) out += '\n';
            out += walk(el);
            sawLine = true;
          } else {
            // Inline wrapper (span, <a>, etc.) — recurse without a line break.
            const inner = walk(el);
            out += inner;
            if (inner.length > 0) sawLine = true;
          }
        }
      });
      return out;
    };

    return { text: walk(root), tags };
  };

  const handleContentEditableInput = (e: React.FormEvent<HTMLDivElement>) => {
    const editable = e.currentTarget;
    const { text: plainText, tags: foundTags } = serializeEditable(editable);

    if (!isControlled) {
      setInternalValue(plainText);
    }
    onChange?.(plainText);

    // Update tag chips if any were removed
    if (foundTags.length !== tagChips.length) {
      setTagChips(foundTags);
      onTagsChange?.(foundTags);
    }

    // Handle autocomplete
    if (onTagSearch) {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      const textNode = range.startContainer;
      
      if (textNode.nodeType === Node.TEXT_NODE) {
        const textContent = textNode.textContent || '';
        const cursorOffset = range.startOffset;
        const textBeforeCursor = textContent.substring(0, cursorOffset);
        const lastAtIndex = textBeforeCursor.lastIndexOf('@');

        if (lastAtIndex !== -1) {
          const afterAt = textBeforeCursor.substring(lastAtIndex + 1);
          
          // Allow spaces in search - only stop if there's a newline or another @
          const hasNewline = afterAt.includes('\n');
          const hasAnotherAt = afterAt.includes('@');
          
          if (!hasNewline && !hasAnotherAt && afterAt.length > 0) {
            const query = afterAt.trim();
            if (query.length > 0) {
              const rect = range.getBoundingClientRect();
              setCursorRect({ top: rect.top, left: rect.left });
              const results = onTagSearch(query);
              if (results && Array.isArray(results)) {
                setTagSuggestions(results);
                setShowTagSuggestions(results.length > 0);
              }
              setSelectedTagIndex(0);
              return;
            }
          }
        }
      }
      
      setShowTagSuggestions(false);
      setCursorRect(null);
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height to scrollHeight (content height) but respect max-height
      const newHeight = Math.min(textarea.scrollHeight, 200); // 200px max height
      textarea.style.height = `${newHeight}px`;
    }
  }, [value]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: async (acceptedFiles, rejectedFiles) => {
      setIsDragOverTextarea(false);
      
      if (acceptedFiles.length > 0) {
        await handleFileUpload(acceptedFiles);
      } else if (rejectedFiles.length > 0) {
        // Try to upload rejected files anyway (we'll validate in handleFileUpload)
        const rejectedFileObjects = rejectedFiles.map(fr => fr.file);
        await handleFileUpload(rejectedFileObjects);
      }
    },
    onDragEnter: () => {
      setIsDragOverTextarea(true);
    },
    onDragLeave: () => {
      setIsDragOverTextarea(false);
    },
    noClick: true,
    noKeyboard: true,
    multiple: true,
    validator: (file) => {
      // Accept all files - we'll filter in handleFileUpload
      return null;
    }
  });

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);

    if (enableTagChips && onTagSearch) {
      const cursorPos = e.target.selectionStart || newValue.length;
      const textBeforeCursor = newValue.substring(0, cursorPos);
      const lastAtIndex = textBeforeCursor.lastIndexOf('@');

      if (lastAtIndex !== -1) {
        const afterAt = textBeforeCursor.substring(lastAtIndex + 1);
        
        // Allow spaces in search - only stop if there's a newline or another @
        const hasNewline = afterAt.includes('\n');
        const hasAnotherAt = afterAt.includes('@');

        if (!hasNewline && !hasAnotherAt && afterAt.length > 0) {
          const query = afterAt.trim();
          if (query.length > 0) {
            const results = onTagSearch(query);
            if (results && Array.isArray(results)) {
              setTagSuggestions(results);
              setShowTagSuggestions(results.length > 0);
              setSelectedTagIndex(0);
            }
          } else {
            setShowTagSuggestions(false);
            setCursorRect(null);
          }
        } else {
          setShowTagSuggestions(false);
          setCursorRect(null);
        }
      } else {
        setShowTagSuggestions(false);
        setCursorRect(null);
      }
    }
  };

  /** Build the attachments array from current documents */
  const buildAttachments = () =>
    allDocuments.map(doc => ({
      id: doc.id,
      name: doc.source === 'artifact' ? formatArtifactTitle(doc.name, doc.version) : doc.name,
      source: doc.source,
      type: (doc.source === 'artifact' || doc.source === 'cab_markdown') ? 'system' as const : 'user' as const,
    }));

  /** Whether the current input state is sendable (see requireTextToSend) */
  const hasSendableContent = () =>
    requireTextToSend ? !!value.trim() : !!(value.trim() || allDocuments.length > 0);

  /** Clear the input after any send-type action */
  const clearInput = () => {
    if (!isControlled) setInternalValue('');
    setAllDocuments([]);
    setTagChips([]);
    onTagsChange?.([]);
    if (enableTagChips && editableRef.current) {
      editableRef.current.innerHTML = '';
    }
  };

  const handleSend = async () => {
    if (!onSend) return;
    if (!hasSendableContent()) return;
    // A handler returns `false` to veto the send (see IChatInputProps.onSend); the
    // input is left as-is so nothing the user typed or tagged is lost.
    if (onSend(value.trim(), buildAttachments(), model, agentSettings) === false) return;
    clearInput();
  };

  /** Queue Message button handler */
  const handleQueue = () => {
    if (!onQueue) return;
    if (!hasSendableContent()) return;
    onQueue(value.trim(), buildAttachments(), model, agentSettings);
    clearInput();
  };

  /** Save Changes button handler (edit mode) */
  const handleSaveEditClick = () => {
    if (!onSaveEdit) return;
    if (!hasSendableContent()) return;
    onSaveEdit(value.trim(), buildAttachments(), model, agentSettings);
    clearInput();
  };

  const scrollSelectedIntoView = (index: number) => {
    suggestionsRef.current?.scrollToIndex(index);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (enableTagChips && showTagSuggestions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const newIndex = Math.min(selectedTagIndex + 1, tagSuggestions.length - 1);
        setSelectedTagIndex(newIndex);
        scrollSelectedIntoView(newIndex);
        return;
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const newIndex = Math.max(selectedTagIndex - 1, 0);
        setSelectedTagIndex(newIndex);
        scrollSelectedIntoView(newIndex);
        return;
      } else if ((e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) && tagSuggestions[selectedTagIndex]) {
        e.preventDefault();
        handleSelectTag(tagSuggestions[selectedTagIndex]);
        return;
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowTagSuggestions(false);
        setCursorRect(null);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (editingJobId) {
        handleSaveEditClick();
      } else if (isQueueMode && onQueue) {
        handleQueue();
      } else if (!isStopOnly) {
        handleSend();
      }
    }
  };

  const handleSelectTag = (tag: TagChip) => {
    if (enableTagChips && editableRef.current) {
      const editable = editableRef.current;
      const selection = window.getSelection();

      // Find the text node containing @ symbol
      let textNode: Node | null = null;
      let lastAtIndex = -1;
      let cursorOffset = 0;

      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        if (range.startContainer.nodeType === Node.TEXT_NODE) {
          textNode = range.startContainer;
          cursorOffset = range.startOffset;
          const textBeforeCursor = (textNode.textContent || '').substring(0, cursorOffset);
          lastAtIndex = textBeforeCursor.lastIndexOf('@');
        }
      }

      // Fallback: search through all text nodes to find one with @
      if (lastAtIndex === -1) {
        const walker = document.createTreeWalker(editable, NodeFilter.SHOW_TEXT);
        let node;
        while (node = walker.nextNode()) {
          const text = node.textContent || '';
          const atIndex = text.lastIndexOf('@');
          if (atIndex !== -1) {
            textNode = node;
            lastAtIndex = atIndex;
            cursorOffset = text.length;
            break;
          }
        }
      }

      if (textNode && lastAtIndex !== -1) {
        const textContent = textNode.textContent || '';
        const beforeAt = textContent.substring(0, lastAtIndex);
        const afterCursor = textContent.substring(cursorOffset);

        // Update text node
        textNode.textContent = beforeAt;

        // Give each chip insertion a unique id so the same object can be tagged multiple times
        const chipId = generateUUID();
        const tagInstance: TagChip = { ...tag, id: chipId };

        const chip = document.createElement('span');
        chip.contentEditable = 'false';
        chip.setAttribute('data-tag-id', chipId);
        chip.className = 'inline-flex items-center gap-1 bg-bg-secondary border border-text-primary rounded px-1.5 py-0.5 mx-0.5 my-0.5 text-xs font-medium text-text-primary font-mono align-middle';
        chip.style.verticalAlign = 'middle';

        const chipContent = document.createElement('span');
        chipContent.textContent = tag.label;
        chip.appendChild(chipContent);

        const deleteBtn = document.createElement('span');
        deleteBtn.innerHTML = '×';
        deleteBtn.className = 'cursor-pointer hover:text-text-error-primary ml-0.5 text-sm leading-none';
        deleteBtn.contentEditable = 'false';
        deleteBtn.setAttribute('data-delete-tag', chipId);
        chip.appendChild(deleteBtn);

        // Insert chip after text node
        if (textNode.parentNode) {
          textNode.parentNode.insertBefore(chip, textNode.nextSibling);

          // Add space after chip
          const spaceNode = document.createTextNode(' ' + afterCursor);
          chip.parentNode?.insertBefore(spaceNode, chip.nextSibling);

          // Move cursor after space
          setTimeout(() => {
            const newRange = document.createRange();
            const sel = window.getSelection();
            newRange.setStart(spaceNode, 1);
            newRange.collapse(true);
            sel?.removeAllRanges();
            sel?.addRange(newRange);
            editable.focus();
          }, 0);
        }

        // Update state - must happen before processing content
        const newTagChips = [...tagChips, tagInstance];
        setTagChips(newTagChips);
        onTagsChange?.(newTagChips);

        // Update value - build it manually to avoid async state issue
        let plainText = '';
        editable.childNodes.forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            plainText += node.textContent || '';
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            const tagId = element.getAttribute('data-tag-id');
            if (tagId) {
              const foundTag = newTagChips.find(t => t.id === tagId);
              if (foundTag) {
                plainText += '@' + foundTag.label;
              }
            }
          }
        });

        if (!isControlled) {
          setInternalValue(plainText);
        }
        onChange?.(plainText);
        setShowTagSuggestions(false);
        setCursorRect(null);
      }
    } else {
      // Fallback to textarea
      const textarea = textareaRef.current;
      if (!textarea) return;

      const cursorPos = textarea.selectionStart || value.length;
      const textBeforeCursor = value.substring(0, cursorPos);
      const lastAtIndex = textBeforeCursor.lastIndexOf('@');

      if (lastAtIndex === -1) return;

      const beforeTag = value.substring(0, lastAtIndex);
      const afterCursor = value.substring(cursorPos);
      const newValue = beforeTag + '@' + tag.label + ' ' + afterCursor;

      if (!isControlled) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);

      const newTagChips = [...tagChips];
      const exists = newTagChips.find(t => t.id === tag.id);
      if (!exists) {
        newTagChips.push(tag);
        setTagChips(newTagChips);
        onTagsChange?.(newTagChips);
      }

      setShowTagSuggestions(false);
      setCursorRect(null);

      setTimeout(() => {
        textarea.focus();
        const newCursorPos = lastAtIndex + tag.label.length + 2;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };

  const handleRemoveTagChip = (tagId: string) => {
    const tagToRemove = tagChips.find(t => t.id === tagId);
    if (!tagToRemove) return;

    const newTagChips = tagChips.filter(t => t.id !== tagId);
    setTagChips(newTagChips);
    onTagsChange?.(newTagChips);

    if (enableTagChips && editableRef.current) {
      const editable = editableRef.current;
      const chipElement = editable.querySelector(`[data-tag-id="${tagId}"]`);
      if (chipElement) {
        chipElement.remove();
        handleContentEditableInput({ currentTarget: editable } as any);
      }
    } else {
      const newValue = value.replace(new RegExp(`@${tagToRemove.label}\\b`, 'g'), '').replace(/\s+/g, ' ').trim();
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    }
  };


  // Paste handler for contentEditable - paste plain text only
  const handleContentEditablePaste = async (e: React.ClipboardEvent<HTMLDivElement>) => {
    // Handle pasted files/images (same as textarea path)
    const items = e.clipboardData?.items;
    if (items) {
      const pastedFiles: File[] = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === 'file') {
          const file = items[i].getAsFile();
          if (file) pastedFiles.push(file);
        }
      }
      if (pastedFiles.length > 0) {
        e.preventDefault();
        await handleFileUpload(pastedFiles);
        return;
      }
    }

    e.preventDefault();
    const text = e.clipboardData?.getData('text/plain') || '';
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();
    const textNode = document.createTextNode(text);
    range.insertNode(textNode);

    // Move cursor to end of inserted text
    range.setStartAfter(textNode);
    range.setEndAfter(textNode);
    selection.removeAllRanges();
    selection.addRange(range);

    // Trigger input to update state
    if (editableRef.current) {
      handleContentEditableInput({ currentTarget: editableRef.current } as React.FormEvent<HTMLDivElement>);
    }
  };

  // Paste handler for textarea
  const handleTextareaPaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    
    const pastedFiles: File[] = [];
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      // Handle both images and files
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          pastedFiles.push(file);
        }
      }
    }
    
    if (pastedFiles.length > 0) {
      e.preventDefault(); // Prevent default paste behavior for files
      await handleFileUpload(pastedFiles);
    }
  };

  const isCanceledUpload = (error: any) => error?.name === 'CanceledError' || error?.name === 'AbortError';

  /**
   * Build-mode upload: checks each file against the policy the upload endpoint
   * enforces, routes by extension (images to the images endpoint), shows the
   * bytes sent, and keeps every file that uploaded even when another one failed.
   */
  const handleBuildUpload = async (fileArray: File[]): Promise<Document[] | undefined> => {
    const policy = await fetchBuildUploadPolicy(projectId);
    const { images, files: otherFiles, errors } = await checkBuildUploads(fileArray, policy);
    if (errors.length > 0) {
      alert(`Cannot upload the following files:\n\n${errors.join('\n')}`);
    }

    const queue = [
      ...images.map(file => ({ file, endpoint: 'images', source: 'image' as const })),
      ...otherFiles.map(file => ({ file, endpoint: 'files', source: 'file' as const })),
    ];
    if (queue.length === 0) return;

    setUploading(true);
    setUploadingFiles(queue.map(item => item.file.name));
    setUploadProgress(0);
    uploadAbortController.current = new AbortController();

    const totalBytes = queue.reduce((sum, item) => sum + item.file.size, 0) || 1;
    const sentBytes = queue.map(() => 0);

    const results = await Promise.allSettled(queue.map((item, index) => {
      const formData = new FormData();
      formData.append('file', item.file);
      return api.post(`/projects/${projectId}/${item.endpoint}/`, formData, {
        params: { mode: 'build' },
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        signal: uploadAbortController.current?.signal,
        onUploadProgress: (event) => {
          // `loaded` counts multipart overhead too, so cap it at the file's size.
          sentBytes[index] = Math.min(event.loaded, item.file.size);
          const sent = sentBytes.reduce((sum, bytes) => sum + bytes, 0);
          setUploadProgress((sent / totalBytes) * 100);
        },
      });
    }));

    const uploadedDocs: Document[] = [];
    const failures: string[] = [];
    results.forEach((result, index) => {
      const { file, source } = queue[index];
      if (result.status === 'fulfilled') {
        const data = result.value.data;
        const name = data.original_name || data.filename || file.name;
        uploadedDocs.push({
          id: data.id,
          name,
          size: data.size || file.size,
          dateModified: data.uploaded_at || new Date().toISOString(),
          type: 'user' as const,
          fileType: name.split('.').pop() || '',
          source,
        });
      } else if (!isCanceledUpload(result.reason)) {
        const response = result.reason?.response;
        const reason = response?.data?.detail
          ?? (response?.status === 413 ? 'The file is larger than the server accepts.' : 'Please try again.');
        failures.push(`"${file.name}": ${reason}`);
      }
    });

    if (uploadedDocs.length > 0) {
      setAllDocuments(prev => [...prev, ...uploadedDocs]);
    }
    setUploading(false);
    setUploadingFiles([]);
    setUploadProgress(null);
    uploadAbortController.current = null;

    if (failures.length > 0) {
      alert(`Failed to upload:\n\n${failures.join('\n')}`);
    }
    return uploadedDocs;
  };

  const handleFileUpload = async (files: File[] | FileList) => {
    if (files instanceof FileList) {
      files = Array.from(files);
    };

    if (attachmentPolicy === 'build') {
      return handleBuildUpload(files as File[]);
    }

    // ── Pre-flight: check filename length before any network call ──
    const fileArray = files as File[];
    const validationErrors = validateFilesForUpload(fileArray);
    if (validationErrors.length > 0) {
      alert(`Cannot upload the following files:\n\n${validationErrors.join('\n')}`);
      return;
    }

    const images = fileArray.filter(file => isImageFile(file));
    const documents = fileArray.filter(file => isDocumentFile(file));

    // Restrict document types when an allowlist is set (images are always allowed).
    // Any unsupported file rejects the whole batch — nothing is uploaded.
    if (allowedDocumentExtensions && allowedDocumentExtensions.length > 0) {
      const unsupported = documents.filter(file =>
        !allowedDocumentExtensions.some(ext => file.name.toLowerCase().endsWith(ext.toLowerCase()))
      );
      if (unsupported.length > 0) {
        alert(
          `Unsupported file type(s):\n${unsupported.map(f => f.name).join('\n')}\n\n` +
          `Only ${allowedDocumentExtensions.join(', ')} documents and images are supported here.`
        );
        return;
      }
    }

    try {
      setUploading(true);
      setUploadingFiles(fileArray.map(f => f.name));
      
      // Create new AbortController for this upload
      uploadAbortController.current = new AbortController();
      
      const uploadPromises = [
        ...images.map(image => {
          const formData = new FormData();
          formData.append('file', image);
          return api.post(`/projects/${projectId}/images/`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            signal: uploadAbortController.current?.signal
          }).then(res => ({ ...res, uploadType: 'image' }));
        }),
        ...documents.map(document => {
          const formData = new FormData();
          formData.append('file', document);
          return api.post(`/projects/${projectId}/files/`, formData, {
            headers: {
              
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            signal: uploadAbortController.current?.signal
          }).then(res => ({ ...res, uploadType: 'file' }));
        })
      ];
      
      const responses = await Promise.all(uploadPromises);

      // Add uploaded files to preview with proper source
      const uploadedDocs: Document[] = responses.map((res: any, idx) => ({
        id: res.data.id,
        name: res.data.original_name || res.data.filename || fileArray[idx].name,
        size: res.data.size || fileArray[idx].size,
        dateModified: res.data.uploaded_at || new Date().toISOString(),
        type: 'user' as const,
        fileType: res.data.original_name ? res.data.original_name.split('.').pop() || '' : '',
        source: res.uploadType // Set source based on upload type (image or file)
      }));
      setAllDocuments(prev => [...prev, ...uploadedDocs]);
      setUploading(false);
      setUploadingFiles([]);
      uploadAbortController.current = null;
      return uploadedDocs;
    } catch (error: any) {
      console.error('Upload error:', error);
      setUploading(false);
      setUploadingFiles([]);
      uploadAbortController.current = null;
      
      // Don't show error alert if upload was cancelled
      if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
        const reason = error?.response?.data?.detail ?? 'Please try again.';
        alert(`Failed to upload files. ${reason}`);
      }
    }
  };

  const handleCancelUpload = () => {
    if (uploadAbortController.current) {
      uploadAbortController.current.abort();
      uploadAbortController.current = null;
    }
    setUploading(false);
    setUploadingFiles([]);
    setUploadProgress(null);
  };

  return (
    <div className={`flex flex-col w-full max-w-[800px] ${className}`} {...getRootProps()}>
      <input {...getInputProps()} />
      <div className="bg-bg-primary border border-border-secondary p-bg-v2-xl rounded-bg-v2-xl container-shadow-md relative">
        {allDocuments.length > 0 && (
          <FilePreview 
            files={allDocuments}
            onRemove={(id) => setAllDocuments(prev => prev.filter(doc => doc.id !== id))} 
          />
        )}
        <div className="relative">
          {enableTagChips ? (
            <>
              <div
                ref={editableRef as any}
                contentEditable={!disabled && !uploading}
                onInput={handleContentEditableInput}
                onKeyDown={handleKeyDown as any}
                onPaste={handleContentEditablePaste}
                onClick={(e) => {
                  const target = e.target as HTMLElement;
                  const deleteTagId = target.getAttribute('data-delete-tag');
                  if (deleteTagId) {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRemoveTagChip(deleteTagId);
                  }
                }}
                suppressContentEditableWarning
                className={`w-full min-h-[40px] max-h-[200px] resize-none text-bg-v2-sm leading-bg-v2-text-sm font-bg-v2-medium text-text-primary bg-transparent border-none outline-none focus:ring-0 overflow-y-auto transition-all ${
                  disabled || uploading ? 'cursor-not-allowed' : ''
                }`}
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              />
              {!value && (
                <div className="absolute top-0 left-0 text-bg-v2-sm leading-bg-v2-text-sm font-bg-v2-medium text-text-placeholder pointer-events-none">
                  {placeholder}
                </div>
              )}
            </>
          ) : (
            <textarea
              ref={textareaRef}
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onPaste={handleTextareaPaste}
              placeholder={placeholder}
              disabled={disabled || uploading}
              rows={1}
              className={`w-full min-h-[40px] max-h-[200px] resize-none text-bg-v2-sm leading-bg-v2-text-sm font-bg-v2-medium text-text-primary placeholder:text-text-placeholder bg-transparent border-none outline-none focus:ring-0 overflow-y-auto transition-all ${
                disabled || uploading ? 'cursor-not-allowed' : ''
              }`}
            />
          )}
          {enableTagChips && showTagSuggestions && (
            <div ref={suggestionsContainerRef}>
              <TagSuggestionList
                ref={suggestionsRef}
                suggestions={tagSuggestions}
                isLoading={isTagSearching}
                selectedIndex={selectedTagIndex}
                onSelect={handleSelectTag}
                cursorRect={cursorRect}
              />
            </div>
          )}
        </div>
        <div className={`flex items-end mt-bg-v2-md ${(showAttachmentButton || showModelSelector || showAgentSettings || showInteractionMode) ? 'justify-between' : 'justify-end'}`}>
          <div className="flex gap-bg-v2-sm">
          {showAttachmentButton && (
            <div className="relative">
              <Button
                label={<Icon name={IconName.Attachment} size="sm" className="text-text-secondary" inheritColor />}
                size="small"
                variant='outline'
                /* Same chip treatment as the mode/model pills beside it: secondary fill,
                   secondary border, and the same hover — in both themes. */
                className="!bg-bg-secondary !border-border-secondary !rounded-bg-v2-full !w-10 !h-10 !p-0 flex items-center justify-center hover:!bg-bg-primary-hover hover:!border-border-primary transition-all duration-150"
                disabled={disabled || uploading}
                onClick={() => setShowFileUpoloadDialog(!showFileUpoloadDialog)}
              />
            </div>
          )}
          {showInteractionMode && (
            <InteractionModeSelector
              mode={interactionMode}
              onChange={(newMode) => onInteractionModeChange?.(newMode)}
              // Allow switching Agent/Ask even while streaming (for queued request)
              disabled={disabled}
              lockedToAsk={interactionModeLockedToAsk}
            />
          )}
          {showModelSelector && (
            <ModelSelector
              model={model}
              onChange={(newModel) => {
                if (isModelControlled && onModelChange) {
                  onModelChange(newModel);
                } else {
                  setInternalModel(newModel);
                }
              }}
              options={MODEL_PREFERENCES}
              // Allow changing model even while streaming (for queued request)
              disabled={disabled}
            />
          )}
          {showAgentSettings && interactionMode !== InteractionMode.ASK && (
            <AgentSettingsMenu
              settings={agentSettings}
              onChange={(newSettings) => {
                if (isAgentSettingsControlled && onAgentSettingsChange) {
                  onAgentSettingsChange(newSettings);
                } else {
                  setInternalAgentSettings(newSettings);
                }
              }}
              // Allow changing settings even while streaming (for queued request)
              disabled={disabled}
            />
          )}
          </div>

          {/* ── Button row: three states ───────────────────────────────── */}
          <div className="flex items-center gap-2">
            {editingJobId ? (
              // ── EDIT MODE: Save Changes + Cancel Edit ─────────────────
              <>
                <Button
                  variant="outline"
                  size="small"
                  label="Cancel Edit"
                  onClick={onCancelEdit}
                  className="hover:!border-border-brand hover:!text-text-brand-primary"
                />
                <Button
                  variant="primary"
                  size="small"
                  label="Save Changes"
                  onClick={handleSaveEditClick}
                  disabled={!hasSendableContent()}
                />
              </>
            ) : isQueueMode || isStopOnly ? (
              // ── STREAMING: Stop button + Queue button (queue mode only) ──
              <>
                {/* Stop button — unchanged design; a spinner, disabled, while stopping */}
                <Button
                  label={
                    // While stopping, a spinner takes the stop glyph's place.
                    <Icon
                      name={isStopping ? IconName.Loading : IconName.Stop}
                      size="lg"
                      className="bg-fg-primary"
                      inheritColor={true}
                      spin={isStopping}
                    />
                  }
                  size="medium"
                  variant='outline'
                  className="!bg-bg-secondary-hover !rounded-bg-v2-full border border-border-error hover:!bg-bg-error-primary disabled:!opacity-100 disabled:hover:!bg-bg-secondary-hover !w-12 !h-12 !p-0 flex items-center justify-center"
                  onClick={onStop}
                  disabled={isStopping}
                  ariaLabel={isStopping ? 'Stopping' : 'Stop'}
                />
                {/* Add-to-queue button — round, mirrors the Send button. Reuses the real
                    Send glyph with a brand "+" badge so it reads as "queue this send". */}
                {isQueueMode && onQueue && (
                  <Button
                    variant="outline"
                    size="medium"
                    label={
                      <span className="relative inline-flex items-center justify-center w-5 h-5">
                        {/* Nudge the plane a hair down-left so the glyph + corner badge read
                            as optically centred inside the round button. */}
                        <Icon name={IconName.SendMessage} size="md" inheritColor className="text-text-primary-on-brand translate-x-[-1px] translate-y-[1px]" />
                        {/* "+" badge — fill is brand-solid (same chartreuse in both themes);
                            plus uses the on-brand text token so it stays legible against that
                            fill. Ring matches the button background to read as a floating badge. */}
                        <span
                          className="absolute -top-1 -right-1 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full"
                          style={{
                            backgroundColor: 'var(--color-bg-brand-solid)',
                            boxShadow: '0 0 0 1.5px var(--color-bg-brand-primary-alt)',
                          }}
                        >
                          <svg width="8" height="8" viewBox="0 0 24 24" fill="none">
                            <path d="M12 5v14M5 12h14" stroke="var(--color-text-primary-on-brand)" strokeWidth="4" strokeLinecap="round" />
                          </svg>
                        </span>
                      </span>
                    }
                    onClick={handleQueue}
                    disabled={!hasSendableContent()}
                    title="Add to queue — runs after the current task finishes"
                    className="!rounded-bg-v2-full !border-border-brand !text-text-brand-primary !bg-bg-brand-primary-alt hover:!bg-bg-brand-primary !w-12 !h-12 !p-0 flex items-center justify-center overflow-visible"
                  />
                )}
              </>
            ) : (
              // ── NORMAL: Send button ───────────────────────────────────
              <Button
                label={
                  (uploading || isSending) ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-text-primary-on-brand"></div>
                  ) : (
                    <Icon name={IconName.SendMessage} size="md" inheritColor className="text-text-primary-on-brand" />
                  )
                }
                size="medium"
                variant='outline'
                className="!bg-bg-brand-solid !rounded-bg-v2-full border-none !w-12 !h-12 !p-0 flex items-center justify-center"
                disabled={disabled || uploading || isSending}
                onClick={handleSend}
              />
            )}
          </div>
        </div>

        {/* Drag Overlay */}
        {(isDragOverTextarea || isDragActive) && !uploading && (
          <div className="absolute inset-0 bg-bg-brand-primary border-2 border-dashed border-border-brand rounded-bg-v2-xl flex items-center justify-center pointer-events-none z-10">
            <div className="flex flex-col items-center gap-bg-v2-lg text-center px-bg-v2-md">
              <Icon name={IconName.Upload} size="lg" variant="brand-utility" />
              <div className="flex flex-col gap-bg-v2-xs">
                <span className="text-bg-v2-sm font-bg-v2-semibold text-text-brand-primary">
                  Drop files here
                </span>
                <span className="text-bg-v2-xs font-bg-v2-regular text-text-tertiary">
                  {attachmentPolicy === 'build'
                    ? 'Documents, code, images, archives, fonts & media supported'
                    : 'Images & Documents supported'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Upload Progress Overlay */}
        {uploading && uploadingFiles.length > 0 && (
          <div className="absolute inset-0 bg-bg-primary border-2 border-border-brand rounded-bg-v2-xl flex items-center justify-center z-20">
            <div className="flex flex-col items-center gap-bg-v2-lg text-center px-bg-v2-md w-full max-w-full">
              {uploadProgress === null && (
                <div className="animate-spin rounded-full w-bg-v2-2xl h-bg-v2-2xl border-2 border-t-transparent border-fg-brand-primary"></div>
              )}
              {uploadProgress !== null && (
                <div className="flex flex-col items-center gap-bg-v2-xs w-full">
                  <ProgressBar value={uploadProgress} label="Uploading files" />
                  <Text variant="text-xs" weight="medium" color="tertiary">
                    {Math.round(uploadProgress)}%
                  </Text>
                </div>
              )}
              <div className="flex flex-col gap-bg-v2-xs">
                <span className="text-bg-v2-sm font-bg-v2-semibold text-text-primary">
                  Uploading in progress...
                </span>
                <div className="flex flex-col gap-bg-v2-xxs mt-bg-v2-sm">
                  {uploadingFiles.map((fileName, index) => (
                    <span 
                      key={index} 
                      className="text-bg-v2-xs font-bg-v2-regular text-text-tertiary truncate max-w-full"
                    >
                      {fileName}
                    </span>
                  ))}
                </div>
              </div>
              {uploadProgress !== null && (
                <Button label="Cancel" size="small" variant="outline" onClick={handleCancelUpload} />
              )}
            </div>
          </div>
        )}
      </div>

      {showAttachmentButton && (
        <FileUploadDialog
          projectId={projectId}
          allowedDocumentExtensions={allowedDocumentExtensions}
          isOpen={showFileUpoloadDialog}
          onClose={() => setShowFileUpoloadDialog(false)}
          onSelect={(files) => {
            // Restrict document types when an allowlist is set (images are always allowed).
            // Any unsupported file rejects the whole selection — the dialog stays open
            // so the user can adjust it.
            if (allowedDocumentExtensions && allowedDocumentExtensions.length > 0) {
              const unsupported = files.filter(file =>
                file.source !== 'image' &&
                !allowedDocumentExtensions.some(ext => file.name.toLowerCase().endsWith(ext.toLowerCase()))
              );
              if (unsupported.length > 0) {
                alert(
                  `Unsupported file type(s):\n${unsupported.map(f => f.name).join('\n')}\n\n` +
                  `Only ${allowedDocumentExtensions.join(', ')} documents and images are supported here.`
                );
                return;
              }
            }
            // Only add files that are not already in allDocuments (to prevent duplicates)
            setAllDocuments(prev => {
              const existingIds = new Set(prev.map(doc => doc.id));
              const newFiles = files.filter(file => !existingIds.has(file.id));
              return [...prev, ...newFiles];
            });
            setShowFileUpoloadDialog(false);
          }}
          onUpload={async (files) => {
            const uploadedDocs = await handleFileUpload(files);
            return uploadedDocs || [];
          }}
          uploading={uploading}
          uploadProgress={uploadProgress}
          acceptAnyFileType={attachmentPolicy === 'build'}
          onCancelUpload={handleCancelUpload}
          onCancel={(uploadedDocumentIds) => {
            // Remove uploaded documents from this session
            setAllDocuments(prev => prev.filter(doc => !uploadedDocumentIds.includes(doc.id)));
          }}
        />
      )}
    </div>
  );
};